// Ranges use min..max; an empty bound means null (unbounded) in the API.
export function parseParameterQuery(value, parameter = {}) {
  const raw = Array.isArray(value) ? value.join(',') : String(value ?? '');
  if (!raw) return null;
  if (parameter.input === 'text') return { valueText: raw };
  if (raw.includes('..') || parameter.input === 'number' || parameter.filter === 'slider') {
    const bounds = raw.split('..');
    if (bounds.length > 2 || bounds.some(bound => bound !== '' && (!bound.trim() || !Number.isFinite(Number(bound))))) return null;
    const valueMin = bounds[0] === '' ? null : Number(bounds[0]);
    const upper = bounds[1] ?? bounds[0];
    const valueMax = upper === '' ? null : Number(upper);
    if (valueMin === null && valueMax === null) return null;
    return valueMin !== null && valueMax !== null
      ? { valueMin: Math.min(valueMin, valueMax), valueMax: Math.max(valueMin, valueMax) }
      : { valueMin, valueMax };
  }
  const idValues = raw.split(',').filter(value => /^\d+$/.test(value)).map(Number);
  if (!idValues.length) return null;
  return { idValues: parameter.filter === 'radio' ? idValues.slice(0, 1) : idValues };
}

export function transformParameterQuery(query, code, parameters = []) {
  return Object.entries(query).flatMap(([key, value]) => {
    const match = key.match(new RegExp(`^${code}\\[(\\d+)\\]$`));
    if (!match) return [];
    const idParameter = Number(match[1]);
    const parameter = parameters.find(item => item.id === idParameter);
    const parsed = parseParameterQuery(value, parameter);
    return parsed ? [{ idParameter, ...parsed }] : [];
  });
}

export function parameterQueryTags(query, code, parameters = []) {
  return transformParameterQuery(query, code, parameters).flatMap(({ idValues, ...item }) =>
    idValues ? idValues.map(idValue => ({ ...item, idValue })) : [item]
  );
}

export function parameterValueLabel(item, parameter = item?.parameter) {
  if (item && ('valueMin' in item || 'valueMax' in item)) {
    if (item.valueMin == null && item.valueMax == null) return '';
    if (item.valueMin == null) return `≤ ${item.valueMax}`;
    if (item.valueMax == null) return `≥ ${item.valueMin}`;
    return item.valueMin === item.valueMax ? item.valueMin : `${item.valueMin} – ${item.valueMax}`;
  }
  if (parameter?.input === 'text') return item?.valueText ?? '';
  if (parameter?.input === 'number') return item?.valueNumber ?? '';
  return item?.value?.value ?? parameter?.values?.find(value => value.id === item?.idValue)?.value ?? '';
}

export function parameterValueQuery(item, parameter = item?.parameter) {
  if (parameter?.input === 'text') return null; // The search API has no text parameter filter.
  if (parameter?.input === 'number') {
    return item?.valueNumber == null ? null : `${item.valueNumber}..${item.valueNumber}`;
  }
  const id = item?.value?.id ?? item?.idValue;
  return id == null ? null : String(id);
}

export function setupParameters(definitions, parameters, query, code, valueIds) {
  return definitions.map(item => {
    const assigned = parameters?.find(value => (value.idParameter ?? value.parameter?.id) === item.id);
    const selected = valueIds ?? parseParameterQuery(query[`${code}[${item.id}]`], item)?.idValues ?? [];
    return {
      idParameter: item.id,
      idValue: assigned?.idValue ?? assigned?.value?.id ?? null,
      valueText: assigned?.valueText ?? null,
      valueNumber: assigned?.valueNumber ?? null,
      input: item.input ?? 'select',
      filter: item.filter,
      name: item.name,
      isRequired: item.isRequired,
      idCategories: item.idCategories,
      types: item.types,
      values: (item.values ?? []).map(value => ({ id: value.id, name: value.value, value: selected.includes(value.id) })),
    };
  });
}

export function parameterValueField(parameter) {
  return { text: 'valueText', number: 'valueNumber' }[parameter.input] || 'idValue';
}

export function hasParameterValue(parameter) {
  const value = parameter[parameterValueField(parameter)];
  if (value == null || String(value).trim() === '') return false;
  return parameter.input === 'number' ? Number.isFinite(Number(value)) : true;
}

export function serializeParameters(parameters = []) {
  return parameters.filter(hasParameterValue).map(parameter => {
    const field = parameterValueField(parameter);
    return {
      idParameter: parameter.idParameter,
      [field]: field === 'valueText' ? parameter[field] : Number(parameter[field]),
    };
  });
}
